import { DialogPanel, DialogTitle } from '@headlessui/react'
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid'
import clsx from 'clsx'
import { useState } from 'react'
import { SyncLoader } from 'react-spinners'
import {
    MonitorMode,
    useCreateMonitorProfileScheduleMutation,
    useGetAccountsInTeamQuery,
    useSearchTeamsQuery
} from '../../api/client'
import Button from '../Button'
import ComboboxMenu from '../inputs/ComboBox'
import { ComboboxMenuOption } from '../inputs/ComboboxMenuOption'
import { toastError, toastSuccess } from '../Notification'
import SimpleModal from '../SimpleModal'
import { getPlatformDisplayName } from '../../util/types'
import { staggerCron } from './cron-stagger'
import { platformFor } from './platforms'

/** The source monitor's settings, as the copies should be created with. */
export type MonitorTemplate = {
    mode: MonitorMode
    windowDays?: number | null
    since?: string | null
    targets: string[]
    scrapeProfile?: string | null
    cron: string
}

type Props = {
    sourceAccountId: number
    template: MonitorTemplate
}

/**
 * One profile's outcome. Keyed by account id rather than handle: a team
 * routinely holds the same handle on two platforms, which collided the
 * React key and left two rows saying the same thing about different
 * profiles.
 */
type Result = {
    accountId: number
    username: string
    platformLabel: string
    ok: boolean
    message: string
}

/** A profile in the chosen team, and whether this schedule can be copied to it. */
type Candidate = {
    id: number
    username: string
    platformLabel: string
    /** The lowercase platform the schedule would be created against. */
    wireValue?: string
    blockedBecause?: string
}

/**
 * Copy this profile's monitoring schedule to the other profiles in a team.
 *
 * Same shape as copying moderation boundaries and keywords: one combobox to
 * find the team, then a confirm listing what will happen. Every copy spends
 * Apify resource on every run, so it earns the confirm.
 */
function CopyMonitoringToTeam({ sourceAccountId, template }: Props) {
    const [teamQuery, setTeamQuery] = useState('')
    const [team, setTeam] = useState<ComboboxMenuOption | undefined>()
    const [confirming, setConfirming] = useState(false)
    const [copying, setCopying] = useState(false)
    const [current, setCurrent] = useState('')
    const [results, setResults] = useState<Result[] | null>(null)

    const { data: teamsData, loading: teamsLoading } = useSearchTeamsQuery({
        variables: { params: { searchTerm: teamQuery } }
    })

    const { data: accountsData, loading: accountsLoading } = useGetAccountsInTeamQuery({
        variables: { params: { teamId: team?.id ?? 0 } },
        skip: !team,
        onError: (error) => toastError(error.message)
    })

    const [createSchedule] = useCreateMonitorProfileScheduleMutation()

    const teamOptions: ComboboxMenuOption[] = (teamsData?.searchTeams ?? []).map((found) => ({
        id: found.id,
        name: found.name
    }))

    // A whole-profile schedule is the same instruction whatever the platform, so
    // the team is the unit, not the platform: each copy is created against the
    // target's own platform and handle.
    const candidates: Candidate[] = (accountsData?.getAccountsInTeam ?? [])
        .filter((account) => account.id !== sourceAccountId && account.serviceAccountUsername)
        .map((account) => {
            const shortName = account.socialMediaServiceShortName ?? ''
            const config = platformFor(shortName)
            const base = {
                id: account.id,
                username: account.serviceAccountUsername ?? '',
                platformLabel: config?.label ?? getPlatformDisplayName(shortName)
            }
            if (account.hasAuthorisation) {
                return {
                    ...base,
                    blockedBecause: 'authorised — its comments arrive through the platform API'
                }
            }
            if (!config) {
                return {
                    ...base,
                    blockedBecause: 'scheduled scraping does not cover this platform'
                }
            }
            if (!config.supportsProfileMode) {
                return {
                    ...base,
                    blockedBecause: 'whole-profile monitoring is the content fetcher’s job here'
                }
            }
            return { ...base, wireValue: config.wireValue }
        })

    const copyable = candidates.filter((candidate) => candidate.wireValue)
    const blocked = candidates.filter((candidate) => candidate.blockedBecause)

    // Specific-posts schedules are per-post, so there is nothing to copy: the
    // other profiles do not have these posts.
    const postsMode = template.mode === MonitorMode.Posts

    const copy = async () => {
        setCopying(true)
        const collected: Result[] = []
        for (const [index, candidate] of copyable.entries()) {
            setCurrent(candidate.username)
            try {
                await createSchedule({
                    variables: {
                        input: {
                            platform: candidate.wireValue as string,
                            serviceAccountUsername: candidate.username,
                            mode: template.mode,
                            windowDays: template.windowDays ?? undefined,
                            since: template.since ?? undefined,
                            scrapeProfile: template.scrapeProfile ?? undefined,
                            // Staggered from the source's time (index 0 is
                            // already the first copy, so it starts one step
                            // after the source itself).
                            cron: staggerCron(template.cron, index + 1)
                        }
                    }
                })
                collected.push({
                    accountId: candidate.id,
                    username: candidate.username,
                    platformLabel: candidate.platformLabel,
                    ok: true,
                    message: 'monitoring created'
                })
            } catch (error: any) {
                collected.push({
                    accountId: candidate.id,
                    username: candidate.username,
                    platformLabel: candidate.platformLabel,
                    ok: false,
                    message: error.message
                })
            }
        }
        setResults(collected)
        setCopying(false)
        setCurrent('')
        const created = collected.filter((result) => result.ok).length
        if (created > 0) {
            toastSuccess(`Monitoring created for ${created} of ${collected.length} profiles.`)
        }
    }

    if (postsMode) {
        return (
            <p className="text-sm text-ink-600">
                This schedule watches a fixed list of posts, which belong to this profile alone, so
                there is nothing to copy to a team. Copying applies to whole-profile monitoring.
            </p>
        )
    }

    return (
        <div>
            <p className="pb-0 mb-0 text-sm text-ink-600">
                You can copy this profile&apos;s monitoring schedule to the other profiles in a team
                below. Profiles Arwen is authorised for are skipped — scraping them would collect
                their comments twice.
            </p>
            <div className="flex flex-row items-end gap-3">
                <ComboboxMenu
                    label="Copy to team"
                    options={teamOptions}
                    value={team}
                    onChange={(option) => {
                        setResults(null)
                        setTeam(option)
                    }}
                    onQueryChange={setTeamQuery}
                    isLoading={teamsLoading}
                    canCreate={false}
                />
                {team ? (
                    <div>
                        <Button
                            primary={false}
                            text="Copy"
                            type="button"
                            size="medium"
                            disabled={accountsLoading || copyable.length === 0}
                            onClick={() => setConfirming(true)}
                        />
                    </div>
                ) : null}
            </div>

            {team && !accountsLoading && copyable.length === 0 ? (
                <p className="mt-2 text-sm text-ink-500">
                    No profiles in that team can take this schedule
                    {blocked.length > 0 ? ' — see the reasons in the list below' : ''}.
                </p>
            ) : null}

            {team && !accountsLoading && blocked.length > 0 && copyable.length === 0 ? (
                <ul className="mt-2 list-none pl-0 space-y-0.5">
                    {blocked.map((candidate) => (
                        <li key={candidate.id} className="text-xs text-ink-500">
                            <span className="text-suspect-700">{candidate.username}</span> (
                            {candidate.platformLabel}) — {candidate.blockedBecause}
                        </li>
                    ))}
                </ul>
            ) : null}

            {results ? (
                <ul className="mt-2 list-none pl-0 space-y-0.5">
                    {results.map((result) => (
                        <li key={result.accountId} className="text-xs">
                            <span className={result.ok ? 'text-safe-700' : 'text-severe-600'}>
                                {result.username}
                            </span>{' '}
                            <span className="text-ink-500">
                                ({result.platformLabel}) — {result.message}
                            </span>
                        </li>
                    ))}
                </ul>
            ) : null}

            <SimpleModal open={confirming} onClose={() => (copying ? null : setConfirming(false))}>
                <DialogPanel
                    className={clsx(
                        'border-primary700 relative transform overflow-hidden rounded-lg',
                        'bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg',
                        'overflow-y-scroll max-h-[500px]'
                    )}
                >
                    <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                        <div className="sm:flex sm:items-start">
                            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-severe-100 sm:mx-0 sm:h-10 sm:w-10">
                                <ExclamationTriangleIcon
                                    className="h-6 w-6 text-severe-600"
                                    aria-hidden="true"
                                />
                            </div>
                            <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                <DialogTitle
                                    as="h3"
                                    className="text-base font-medium leading-6 text-ink-900"
                                >
                                    Copy monitoring to {copyable.length}{' '}
                                    {copyable.length === 1 ? 'profile' : 'profiles'}?
                                </DialogTitle>
                                <div className="mt-2 space-y-4">
                                    <p className="text-sm text-ink-500">
                                        Each of these gets its own daily monitor, and each one
                                        spends Apify resource every time it runs. A profile that is
                                        already monitored keeps the schedule it has.
                                    </p>
                                    <p className="text-sm text-ink-500">
                                        Whole profile, {template.cron} — each copy runs a couple of
                                        minutes after the last, so the team doesn&apos;t hit Apify
                                        all at once.
                                    </p>
                                    <p>These profiles will be monitored:</p>
                                    <ul>
                                        {copyable.map((candidate) => (
                                            <li key={candidate.id}>
                                                {candidate.username}{' '}
                                                <span className="text-ink-500">
                                                    ({candidate.platformLabel})
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                    {blocked.length > 0 ? (
                                        <>
                                            <p className="text-suspect-700">
                                                These will be skipped:
                                            </p>
                                            <ul>
                                                {blocked.map((candidate) => (
                                                    <li
                                                        key={candidate.id}
                                                        className="text-suspect-700"
                                                    >
                                                        {candidate.username}{' '}
                                                        <span className="text-ink-500">
                                                            ({candidate.platformLabel}) —{' '}
                                                            {candidate.blockedBecause}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-ink-50 px-4 py-3 sm:flex sm:flex-row sm:px-6 space-x-6">
                        {results ? (
                            <Button
                                primary={false}
                                text="Close"
                                type="button"
                                onClick={() => setConfirming(false)}
                            />
                        ) : (
                            <>
                                <Button
                                    primary={true}
                                    text="Copy"
                                    type="button"
                                    disabled={copying}
                                    onClick={copy}
                                />
                                {!copying ? (
                                    <Button
                                        primary={false}
                                        text="Cancel"
                                        type="button"
                                        onClick={() => setConfirming(false)}
                                    />
                                ) : null}
                            </>
                        )}
                        {copying ? (
                            <div className="flex flex-row items-baseline">
                                <div className="mx-4">
                                    <SyncLoader loading={true} color="#dc3b32" size={'4px'} />
                                </div>
                                <div className="ml-3">{current}</div>
                            </div>
                        ) : null}
                    </div>
                </DialogPanel>
            </SimpleModal>
        </div>
    )
}

export default CopyMonitoringToTeam
